<script setup lang="ts">
import { useDisplay, useTheme } from 'vuetify'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { formatDateOnly, getPersonDisplayName } from '@/utils/formatters'
import { useCitizenInquiriesStore } from '../../stores/citizenInquiriesStore'
import type { IAppealChannel } from '../../types/IAppeal'
import AppealChannelFormModal from './AppealChannelFormModal.vue'
import { APPEALS_ANCHORS } from '@/modules/onboarding/scenarios/appeals/anchors'

const theme = useTheme()
const { smAndDown } = useDisplay()
const store = useCitizenInquiriesStore()

const search = ref<string | null>('')
const formOpen = ref(false)
const editingChannel = ref<IAppealChannel | null>(null)
const deleteOpen = ref(false)
const channelToDelete = ref<IAppealChannel | null>(null)

const headers = [
  { title: 'Канал поступления', key: 'name', sortable: false },
  { title: 'Тип канала', key: 'is_system', sortable: false },
  { title: 'Создал', key: 'created_by', sortable: false },
  { title: 'Дата создания', key: 'created_at', sortable: false, maxWidth: '10rem' },
  { title: 'Действия', key: 'actions', sortable: false, maxWidth: '7rem', align: 'center' as const },
]

// Каналов заведомо меньше страницы бэка (50) — ищем по уже загруженному списку
const filteredChannels = computed(() => {
  const q = (search.value ?? '').trim().toLowerCase()
  if (!q) return store.channels
  return store.channels.filter(item => item.name.toLowerCase().includes(q))
})

onMounted(() => {
  store.fetchChannels(false)
})

function openCreate() {
  editingChannel.value = null
  formOpen.value = true
}

function openEdit(channel: IAppealChannel) {
  editingChannel.value = channel
  formOpen.value = true
}

function askDelete(channel: IAppealChannel) {
  channelToDelete.value = channel
  deleteOpen.value = true
}

async function confirmDelete(ok: boolean) {
  deleteOpen.value = false
  const channel = channelToDelete.value
  channelToDelete.value = null
  if (!ok || !channel) return
  try {
    await store.deleteChannel(channel.id)
  } catch {
    // Ошибка уже показана стором
  }
}
</script>

<template>
  <div>
    <!-- Шапка и таблица по эталону справочника групп исполнителей (WorkGroupsTable.vue) -->
    <header :class="{ 'd-flex flex-column gap-2 mb-4': smAndDown }">
      <div :class="smAndDown ? 'd-flex flex-column gap-2 w-100' : 'd-flex align-center justify-space-between w-100'">
        <VBtn
          :class="{ 'w-100': smAndDown }"
          variant="elevated"
          prepend-icon="ri-add-line"
          class="general-button"
          :data-onboarding="APPEALS_ANCHORS.settings.addChannel"
          @click="openCreate"
        >
          Добавить канал
        </VBtn>
      </div>
    </header>

    <div class="wrapper">
      <div
        class="d-flex align-center gap-2 pa-4"
        :class="{ 'flex-column': smAndDown }"
      >
        <VTextField
          v-model="search"
          class="flex-grow-0"
          :class="smAndDown ? 'w-100' : 'w-25'"
          variant="outlined"
          clearable
          placeholder="Фильтр по ключевому слову"
          autocomplete="off"
          type="text"
        />
      </div>

      <VDataTable
        :data-onboarding="APPEALS_ANCHORS.settings.channels"
        :items="filteredChannels"
        :headers="headers"
        :items-per-page="-1"
        :loading="store.isChannelsLoading"
        loading-text=""
        class="no-row-gaps custom-data-table"
        item-value="id"
        :class="[{ 'dark-theme': theme.global.current.value.dark }]"
      >
        <template #no-data>
          <div class="text-center text-italic py-4">
            {{ search?.trim() ? 'Ничего не найдено' : 'Каналы поступления ещё не добавлены' }}
          </div>
        </template>

        <template #[`item.name`]="{ item }">
          <span>{{ item.name }}</span>
        </template>

        <template #[`item.is_system`]="{ item }">
          <span>{{ item.is_system ? 'Системный' : 'Пользовательский' }}</span>
        </template>

        <template #[`item.created_by`]="{ item }">
          <span>{{ getPersonDisplayName(item.created_by) || '—' }}</span>
        </template>

        <template #[`item.created_at`]="{ item }">
          <span v-if="item.created_at">{{ formatDateOnly(item.created_at) }}</span>
        </template>

        <template #[`item.actions`]="{ item }">
          <div v-if="!item.is_system" class="d-flex align-center justify-center gap-1">
            <VIconBtn
              :rounded="false"
              :size="32"
              icon="ri-edit-line"
              variant="text"
              @click.stop="openEdit(item)"
            />
            <VIconBtn
              :rounded="false"
              :size="32"
              icon="ri-delete-bin-3-line"
              variant="text"
              @click.stop="askDelete(item)"
            />
          </div>
        </template>

        <template #bottom />
      </VDataTable>
    </div>

    <AppealChannelFormModal
      v-model="formOpen"
      :channel="editingChannel"
    />
    <ConfirmDialog
      v-model:is-dialog-visible="deleteOpen"
      :confirmation-question="`Удалить канал «${channelToDelete?.name ?? ''}»?`"
      confirmation-text="Канал пропадёт из списка при регистрации обращений."
      confirm-btn="Удалить"
      reject-btn="Отменить"
      @confirm="confirmDelete"
    />
  </div>
</template>

<style scoped lang="scss">
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.v-data-table__td) {
  padding-block: 0.75rem !important;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

:deep(.custom-data-table tr):hover {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

.text-italic {
  font-style: italic;
}

header {
  background: rgb(var(--v-theme-background));
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
